import { useState } from "react"
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  PlayCircleFilled,
} from "@ant-design/icons"
import { Badge, Button, Flex, Modal, Segmented, Tag, Typography } from "antd"
import classNames from "classnames"
import { VideoPlayer } from "~/components/VideoPlayer"
import styles from "./index.css"

const { Text, Title } = Typography

type ShowcaseCaseKey = "earrings" | "hat" | "pants"

type ShowcaseVideo = {
  poster?: string
  url: string
}

type ShowcaseCase = {
  category: string
  key: ShowcaseCaseKey
  label: string
  originalVideo: ShowcaseVideo
  recreatedVideo: ShowcaseVideo
  title: string
}

const SHOWCASE_CASES: ShowcaseCase[] = [
  {
    category: "帽子",
    key: "hat",
    label: "案例 01 · 帽子",
    originalVideo: {
      url: "https://cdn-testing.duobaogongxiao.com/kada-uploads/1785406383661-1fr69vkp-___2.0.mp4",
      poster:
        "https://cdn-testing.duobaogongxiao.com/kada-uploads/1785821390993-9sosvdet-20260804-124008.jpg",
    },
    recreatedVideo: {
      url: "https://cdn-testing.duobaogongxiao.com/kada-uploads/1785406381094-3lja0wz7-_______.mp4",
      poster:
        "https://cdn-testing.duobaogongxiao.com/kada-uploads/1785821390882-0eppuk7x-20260804-124003.jpg",
    },
    title: "日常百搭遮阳帽 · 轻盈出街",
  },
  {
    category: "裤子",
    key: "pants",
    label: "案例 02 · 裤子",
    originalVideo: {
      url: "https://cdn-testing.duobaogongxiao.com/kada-uploads/1785137148305-9bhtbg2s-_1_____.mp4",
      poster:
        "https://cdn-testing.duobaogongxiao.com/kada-uploads/1785821390780-wtda07ar-20260804-123957.jpg",
    },
    recreatedVideo: {
      url: "https://cdn-testing.duobaogongxiao.com/kada-uploads/1785137146751-4b1qvc7h-_1______.mp4",
      poster:
        "https://cdn-testing.duobaogongxiao.com/kada-uploads/1785821390687-cfirj34z-20260804-123951.jpg",
    },
    title: "夏季新款运动裤 · 清凉上市",
  },
  {
    category: "耳饰",
    key: "earrings",
    label: "案例 03 · 耳饰",
    originalVideo: {
      url: "https://cdn-testing.duobaogongxiao.com/kada-uploads/1785405518338-21rprzkg-___.mp4",
      poster:
        "https://cdn-testing.duobaogongxiao.com/kada-uploads/1785821390529-dvqv6sd3-20260804-123929.png",
    },
    recreatedVideo: {
      url: "https://cdn-testing.duobaogongxiao.com/kada-uploads/1785826869166-262u4sqc-_______.mp4",
      poster:
        "https://cdn-testing.duobaogongxiao.com/kada-uploads/1785821390020-uon9mj93-20260804-123922.png",
    },
    title: "精致通勤耳饰 · 氛围感加分",
  },
]

const SHOWCASE_OPTIONS = SHOWCASE_CASES.map(({ key, label }) => ({
  label,
  value: key,
}))

type ProductShowcaseVideoPreviewProps = ShowcaseVideo & {
  autoPlay?: boolean
  compact?: boolean
  label: string
  loop?: boolean
  recreated?: boolean
}

export const ProductShowcaseVideoPreview = ({
  autoPlay,
  compact,
  label,
  loop,
  poster,
  recreated,
  url,
}: ProductShowcaseVideoPreviewProps) => {
  const previewClassName = compact
    ? styles.compactVideoPreview
    : styles.caseVideoPreview

  return (
    <div
      className={classNames(
        styles.videoPreviewShell,
        compact ? styles.compactVideoShell : styles.caseVideoShell,
      )}
    >
      {url ? (
        <VideoPlayer
          controls
          muted
          objectFit="cover"
          autoPlay={autoPlay}
          className={previewClassName}
          loop={loop}
          poster={poster}
          src={url}
        />
      ) : (
        <Flex
          align="center"
          className={classNames(styles.videoPlaceholder, previewClassName)}
          gap={8}
          justify="center"
          vertical
        >
          <PlayCircleFilled />
          <Text type="secondary">视频待配置</Text>
        </Flex>
      )}
      <Badge
        className={styles.videoLabel}
        status={recreated ? "error" : "success"}
        text={label}
      />
    </div>
  )
}

type CaseModalProps = {
  open: boolean
  onCancel: () => void
}

export const CaseModal = ({ open, onCancel }: CaseModalProps) => {
  const [activeCaseKey, setActiveCaseKey] = useState<ShowcaseCaseKey>(
    SHOWCASE_CASES[0].key,
  )
  const activeCaseIndex = SHOWCASE_CASES.findIndex(
    ({ key }) => key === activeCaseKey,
  )
  const activeCase = SHOWCASE_CASES[activeCaseIndex]

  const changeCase = (offset: number) => {
    const nextIndex =
      (activeCaseIndex + offset + SHOWCASE_CASES.length) % SHOWCASE_CASES.length

    setActiveCaseKey(SHOWCASE_CASES[nextIndex].key)
  }

  return (
    <Modal
      centered
      footer={null}
      open={open}
      title={
        <Flex vertical>
          <Title
            className={styles.modalTitle}
            level={4}
          >
            爆款复刻案例
          </Title>
          <Text type="secondary">来自真实用户的复刻案例</Text>
        </Flex>
      }
      width={920}
      onCancel={onCancel}
    >
      <Flex
        className={styles.caseModalContent}
        gap={22}
        vertical
      >
        <Segmented
          className={styles.caseSegmented}
          options={SHOWCASE_OPTIONS}
          value={activeCaseKey}
          onChange={(value) => setActiveCaseKey(value as ShowcaseCaseKey)}
        />
        <Flex
          align="center"
          gap={10}
        >
          <Tag
            color="orange"
            variant="filled"
          >
            {activeCase.category}
          </Tag>
          <Text strong>{activeCase.title}</Text>
        </Flex>
        <Flex
          className={styles.caseComparison}
          align="center"
          gap={18}
        >
          <ProductShowcaseVideoPreview
            label="原视频"
            {...activeCase.originalVideo}
          />
          <ArrowRightOutlined className={styles.comparisonArrow} />
          <ProductShowcaseVideoPreview
            label="复刻视频"
            recreated
            {...activeCase.recreatedVideo}
          />
        </Flex>
        <Flex
          className={styles.caseNavigation}
          align="center"
          justify="space-between"
        >
          <Button
            icon={<ArrowLeftOutlined />}
            type="text"
            onClick={() => changeCase(-1)}
          >
            上一组
          </Button>
          <Flex gap={7}>
            {SHOWCASE_CASES.map(({ key, label }) => (
              <button
                aria-label={`切换到${label}`}
                aria-pressed={key === activeCaseKey}
                className={classNames(
                  styles.caseDot,
                  key === activeCaseKey && styles.caseDotActive,
                )}
                key={key}
                type="button"
                onClick={() => setActiveCaseKey(key)}
              />
            ))}
          </Flex>
          <Button
            icon={<ArrowRightOutlined />}
            iconPlacement="end"
            type="text"
            onClick={() => changeCase(1)}
          >
            下一组
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}
